/* eslint-disable class-methods-use-this */
import filter from 'lodash/filter';
import first from 'lodash/first';
import groupBy from 'lodash/groupBy';
import uniqueId from 'lodash/uniqueId';
import SVG, { RBox } from 'svg.js';
import eventbus from 'tiny-emitter/instance';

// import ApiService from '@/core/utils/apiService';
import {
  CATEGORY_TYPES,
  EVENTSTATUS,
  GRIDSNAP,
  LAYOUT,
  MODE,
  RENDERER_MODE,
  SECTIONSTATUS,
  TYPE,
  ZOOM,
} from '@/constants';
import store from '@/store/store';
import * as FileSaver from 'file-saver';
import Seatmap from './seatmap';

import BlockSeatsMode from './mode/blockseats/index';
import CategoriesMode from './mode/categories/index';
import DrawingMode from './mode/drawing/index';
import GroupsMode from './mode/group/index';
import LabelsMode from './mode/labels/index';
import PosMode from './mode/pos/index';
import RendererMode from './mode/renderer/index';

import { Category } from '@/store/modules/categories';
import { POS } from '@/store/modules/pos';
import { useState } from '@/store/useStore';
import EditSection from './drawing/edit.section';
import Selection from './drawing/selection';
import SelectionRenderer from './drawing/selection.renderer';
import { Unit } from './models';
import { GA } from './models/ga/ga';
import { Seat } from './models/seat';
import { Section } from './models/sections/section';
import { TableSeat } from './models/tables/tableSeat';
import { ZoomManager } from './ZoomManager';

let singleton: Application | null = null;

const modeMappings = {
  [MODE.DRAWING]: DrawingMode,
  [MODE.LABELS]: LabelsMode,
  [MODE.GROUP]: GroupsMode,
  [MODE.CATEGORIES]: CategoriesMode,
  [MODE.POS]: PosMode,
  [MODE.BLOCKSEATS]: BlockSeatsMode,
  [MODE.RENDERER]: RendererMode,
} as const;

type ModeMapping = typeof modeMappings;
type ModeMappingKeys = keyof ModeMapping;

export type AppProps = {
  disablePanZoom?: boolean;
  onSectionSwitch?: (section: any, selected: boolean) => void;
  refreshCategoryList: () => any;
  refreshPOSList: () => Array<POS>;
  posList: any;
  categoriesList: Array<Category>;
  categoryID: number | null;
  posID: number | null;
  status: number;
  json: any;
  name: string;
  isShowCart: boolean;
  bgImage: string;
  isHideRowLabels: boolean;
  revealSectionsMode: string;
  channel: string;
  options: { modes: Array<string>; layouts?: Array<string> };
  isGapAvoidance: boolean;
  onSeatLimit: (catMaxExceeded: boolean, limit: number) => void;
  seatLimit: number;
  showDownloadButton: boolean;
  onCancel?: () => void;
  onSave: (id: string, seatmapName: string, json: string, status: number, extras: any) => any | Promise<any>;
  id: string;
  extras: any;
  sections?: {
    items: Array<{
      id: string;
      name: string;
      entrance: string;
      code: string;
      level: string;
      categoryId: number;
      thumbnail?: string;
    }>;
    protected: Array<'name' | 'entrance' | 'code' | 'level'>;
  };
  sectionPreviews?: {
    id: string;
    thumbnail: string;
  }[];
  domELM: HTMLElement;
  onClose: () => void;
  isShowLocks: boolean;
  isShowLoading: boolean;
  isHideSectionLabels?: boolean;
  oneCatPerGa: boolean;
  onImageUpload?: (dataUri: string) => void;
  onImageDelete?: () => void;
  holdTypes?: Array<{ key: string; value: string }>;
  allowedHolds?: Array<string>;
  onSectionClick?: (section: Section, isBestAvailable: boolean) => void;
  onSectionOver?: (section: Section) => void;
  onSectionOut?: (section: Section) => void;
  seatsInCartById?: (number | string)[];
  seatsInCartByLabel?: { sectionId?: string; categoryId: string | number; rowLabel: string; label: string }[];
};

export default class Application {
  commandKeyPressed = false;
  hotKeyPressed = false;
  mobileToolTipStatus = false;
  longPressStart = 0;
  longPressEnd = 0;
  mobileToolTipShowTime = 0;
  currentSection: Section | null = null;
  currentSeatSection: Section | null = null;
  unassigned = 0;
  unassignedGa = 0;
  unlabeled = 0;
  conflicted = 0;
  gridPattern: SVG.Pattern | null = null;
  selectionMode: Selection | SelectionRenderer | null = null;
  currentMode: InstanceType<ModeMapping[ModeMappingKeys]> | null = null;
  layoutMode: any = null;
  layout: (typeof LAYOUT)[keyof typeof LAYOUT] = LAYOUT.NOTSELECTED;
  pt?: SVGPoint;
  conflictedzones: Array<string> = [];
  grid: SVG.Rect | undefined = undefined;
  coordinates: SVG.G | undefined = undefined;
  bgImage: SVG.Image | undefined = undefined;
  hasBgImage = false;

  declare uuid: string;
  declare svg: SVG.Doc;
  declare canvas: SVG.G;
  declare props: AppProps;
  declare seatmap: Seatmap;
  declare zoomManager: ZoomManager;

  constructor(svg: SVG.Doc, props: AppProps) {
    if (singleton) return singleton;
    this.uuid = uniqueId(`${this.constructor.name}_`);
    this.svg = svg;
    if (props.disablePanZoom) {
      this.svg.addClass('disable-panzoom');
    }
    this.canvas = svg.group().addClass('canvas');
    this.props = props;
    this.seatmap = new Seatmap(this);
    this.zoomManager = new ZoomManager(this);
    this.initHelpers();
    this.subscribeToEvents();
    singleton = this;
    if (RENDERER_MODE) {
      this.initRendererMode();
      this.onModeChanged(useState('settings').mode.value);
    }
  }

  keyDownHandler(e: KeyboardEvent) {
    const isCommandKeyPressed = e.ctrlKey === true || e.metaKey === true;
    this.commandKeyPressed = isCommandKeyPressed;
    this.hotKeyPressed = !!(e.key === 'a');
  }
  mobileToolTipTouchStart(e: TouchEvent) {
    this.longPressStart = e.timeStamp;
  }
  mobileToolTipTouchEnd(e: TouchEvent) {
    this.longPressEnd = e.timeStamp;
    this.mobileToolTipShowTime = this.longPressEnd - this.longPressStart;
    if (this.mobileToolTipShowTime > 500) {
      eventbus.emit('tooltip:show');
    } else {
      eventbus.emit('tooltip:hide');
    }
  }
  keyUpHandler() {
    this.commandKeyPressed = false;
    this.seatmap.bulkSelected = [];
  }

  static get instance() {
    if (!singleton) throw new Error('No application instance');
    return singleton;
  }

  get unassignedSections() {
    return this.seatmap.sections.filter((s) => !s.categoryId);
  }

  get snapToGrid() {
    return store.state.camera.snapToGrid;
  }

  get unsavedChanges() {
    return store.state.seatmap.unsavedChanges;
  }

  get showLabels() {
    return store.state.seatmap.showLabels;
  }

  get showLocks() {
    return store.state.seatmap.showLocks;
  }

  get isChrome() {
    // @ts-expect-error
    return !!window.chrome && !!window.chrome.webstore;
  }

  get isFirefox() {
    // @ts-expect-error
    return typeof InstallTrigger !== 'undefined';
  }

  get isIE() {
    // @ts-expect-error
    return /*@cc_on!@*/ false || !!document.documentMode;
  }

  get isEdge() {
    // @ts-expect-error
    return !this.isIE && !!window.StyleMedia;
  }

  get isSafari() {
    return (
      // @ts-expect-error
      /constructor/i.test(window.HTMLElement) ||
      // eslint-disable-next-line func-names
      (function (p) {
        return p.toString() === '[object SafariRemoteNotification]';
        // eslint-disable-next-line no-undef
        // @ts-expect-error
      })(!window.safari || (typeof safari !== 'undefined' && safari.pushNotification))
    );
  }

  get isAndroid() {
    return navigator.userAgent.match(/Android/i);
  }

  get isBlackBerry() {
    return navigator.userAgent.match(/BlackBerry/i);
  }

  get isIOS() {
    return navigator.userAgent.match(/iPhone|iPad|iPod/i);
  }

  get isOpera() {
    return navigator.userAgent.match(/Opera Mini/i);
  }

  get isWindows() {
    return navigator.userAgent.match(/IEMobile/i) || navigator.userAgent.match(/WPDesktop/i);
  }

  get isMobileDevice() {
    return this.isAndroid || this.isBlackBerry || this.isIOS || this.isOpera || this.isWindows;
  }

  getCategoryColor(catid: number) {
    if (store.state.categories.categories) {
      const category = store.state.categories.categories.find((c) => c.id === catid);
      if (category) return category.color;
    }
    return null;
  }

  getCategoryObj(catid: number | null | undefined) {
    if (store.state.categories.categories) {
      const category = store.state.categories.categories.find((c) => c.id === catid);
      return category;
    }
    return null;
  }

  getFilteredSeats(sid: string | null = null): Array<Seat | TableSeat> {
    let { seatsAndTableSeats } = this.seatmap;
    if (sid) {
      seatsAndTableSeats = filter(seatsAndTableSeats, (seat) => ('table' in seat ? seat.table.sid : seat.sid) === sid);
    }
    return seatsAndTableSeats;
  }

  getFilteredGa(isInsideZoneLayout = true) {
    let { gaList } = this.seatmap;
    if (isInsideZoneLayout && this.currentSection) {
      gaList = filter(gaList, (ga) => ga.sid === this.currentSection!.uuid);
    }
    return gaList;
  }

  getPosColor(posid: number) {
    const pos = store.state.pos.pos.find((p) => p.id === posid);
    return pos ? pos.color : '';
  }

  getPosObj(posid: number) {
    if (store.state.pos.pos) {
      const pos = store.state.pos.pos.find((p) => p.id === posid);
      return pos;
    }
    return null;
  }

  isRecalculateConflicts() {
    //return store.state.settings.previousMode === MODE.LABELS;
    // NEW : Always recalculate conflicts
    return true;
  }

  isShowConflicts() {
    /*if (
      (
        (
          this.props.status === EVENTSTATUS.PAUSED ||
          this.props.status === EVENTSTATUS.DRAFT
        ) &&
        store.state.settings.mode === MODE.LABELS
      ) ||
      this.props.status === EVENTSTATUS.PUBLISHED
    ) {
      return true;
    }
    return false;
    */
    // NEW : Always recalculate conflicts
    return true;
  }

  isRecalculatePosColors() {
    return store.state.settings.previousMode === MODE.POS;
  }

  isShowPosColor() {
    if (
      (this.props.status === EVENTSTATUS.PAUSED || this.props.status === EVENTSTATUS.DRAFT) &&
      store.state.settings.mode === MODE.POS
      // || this.props.status === EVENTSTATUS.PUBLISHED
    ) {
      return true;
    }
    return false;
  }

  refreshPosList(refresh = false) {
    let posList: Array<POS>;
    if (refresh) {
      posList = this.props.refreshPOSList();
      this.props.posList = posList;
    } else {
      posList = this.props.posList;
    }
    if (this.seatmap.selection && this.seatmap.selection.length > 0) {
      const selection = this.seatmap.selection.items;
      const newPosList: Array<POS> = [];
      if (selection.length === 1 && selection[0].type === TYPE.GA) {
        posList.forEach((item) => {
          const foundPos = selection.find(
            (a) => a.attributes.pos && a.attributes.pos[item.id] && a.attributes.pos[item.id] !== 0
          );
          if (foundPos) {
            newPosList.push(item);
          }
        });
      } else {
        posList.forEach((item) => {
          const foundPos = selection.find((a) => a.attributes.posId === item.id);
          if (foundPos) {
            newPosList.push(item);
          }
        });
      }
      posList.forEach((item) => {
        const foundPos = newPosList.find((a) => a.id === item.id);
        if (!foundPos) {
          newPosList.push(item);
        }
      });
    }
    store.commit('pos/updatePosList', posList);
  }

  refreshCategoryList(refresh = false, unit: Unit | null = null) {
    let catList: Array<Category>;
    let type = null;
    if (refresh) {
      catList = this.props.refreshCategoryList();
      this.props.categoriesList = catList;
    } else {
      catList = this.props.categoriesList;
    }
    const catCollection = JSON.parse(JSON.stringify(catList));
    const selection = this.seatmap.selection.items;
    if (unit) {
      type = unit.type;
    } else {
      type = first(Object.keys(groupBy(selection, 'type')));
    }
    if (type) {
      if (TYPE.TABLE === type || TYPE.TABLESEAT === type || TYPE.SEAT === type) {
        catList = filter(catList, { type: CATEGORY_TYPES.SEATED });
      } else if (TYPE.GA === type) {
        catList = filter(catList, { type: CATEGORY_TYPES.GA });
      }
    }
    const newCategoriesList: Array<Category> = [];
    if (RENDERER_MODE) {
      catList.forEach((item) => {
        newCategoriesList.push(item);
      });
    } else {
      catList.forEach((item) => {
        const foundCategory = selection.find((a) => a.attributes.categoryId === item.id);
        if (foundCategory) {
          newCategoriesList.push(item);
        }
      });
      catList.forEach((item) => {
        const foundCategory = newCategoriesList.find((a) => a.id === item.id);
        if (!foundCategory) {
          newCategoriesList.push(item);
        }
      });
    }
    store.commit('categories/updateCategoryCollection', catCollection);
    store.commit('categories/updateCategories', newCategoriesList);
    return newCategoriesList;
  }

  getCategories() {
    return store.state.categories.categories;
  }

  initHelpers() {
    this.pt = (this.svg.node as unknown as SVGSVGElement).createSVGPoint();
    this.addBrightnessFilter();
  }

  addBrightnessFilter() {
    this.svg.svg(`<filter id="brightness">
                    <feComponentTransfer>
                      <feFuncR type="linear" slope="0.7"/>
                      <feFuncG type="linear" slope="0.7"/>
                      <feFuncB type="linear" slope="0.7"/>
                    </feComponentTransfer>
                  </filter>`);
  }

  snap(value: number, gridSize = GRIDSNAP) {
    return this.snapToGrid ? gridSize * Math.round(value / gridSize) : value;
  }

  subscribeToEvents() {
    SVG.on(window, 'keydown', this.keyDownHandler, this);
    SVG.on(window, 'keyup', this.keyUpHandler, this);
    SVG.on(window, 'touchstart', this.mobileToolTipTouchStart, this);
    SVG.on(window, 'touchend', this.mobileToolTipTouchEnd, this);
    eventbus.on('seatmap:download', this.onDownloadSeatmap, this);
    eventbus.on('seatmap:mode:changed', this.onModeChanged, this);
    eventbus.on('layout:changed', this.onLayoutChanged, this);
  }

  destroyEventListeners() {
    SVG.off(window, 'keydown', this.keyDownHandler);
    SVG.off(window, 'keyup', this.keyUpHandler);
    SVG.off(window, 'touchstart', this.mobileToolTipTouchStart, this);
    SVG.off(window, 'touchend', this.mobileToolTipTouchEnd, this);

    eventbus.off('seatmap:download', this.onDownloadSeatmap);
    eventbus.off('seatmap:mode:changed', this.onModeChanged);
    eventbus.off('layout:changed', this.onLayoutChanged);
  }

  onDownloadSeatmap(settings: Record<string, any>) {
    const seatmapJson = this.seatmap.toJSON();
    const saveSettings = Object.assign({}, settings);
    delete saveSettings.seatmapName; // Remove seatmapName
    const json = {
      seatmap: { ...seatmapJson, ...saveSettings },
      version: this.props.json ? this.props.json.version : 2,
    };
    const seatmapName = `${settings.seatmapName}.json`;
    const file = new File([JSON.stringify(json)], seatmapName, { type: 'application/json;charset=utf-8' });
    FileSaver.saveAs(file);
  }

  onLayoutChanged(layout: (typeof LAYOUT)[keyof typeof LAYOUT]) {
    this.layout = layout;
    this.destroyLayoutMode();
    switch (layout) {
      case LAYOUT.SECTIONS:
        this.initZonesLayout();
        break;
      case LAYOUT.INSIDESECTION:
        this.initInsideZoneLayout();
        break;
      default:
        break;
    }
  }

  onModeChanged(mode: (typeof MODE)[keyof typeof MODE]) {
    this.destroyCurrentMode();
    if (mode in modeMappings) {
      this.currentMode = new modeMappings[mode as ModeMappingKeys](this);
    }
  }

  onSelectionModeChanged(flag: boolean) {
    return flag === true ? this.initSelectionMode() : this.destroySelectionMode();
  }

  destroyCurrentMode() {
    if (this.currentMode) {
      this.currentMode.destroy();
      this.currentMode = null;
    }
  }

  initSelectionMode() {
    this.selectionMode = new Selection(this);
  }

  initRendererMode() {
    this.selectionMode = new SelectionRenderer(this);
  }

  destroySelectionMode() {
    if (this.selectionMode) {
      this.selectionMode.destroy();
      this.selectionMode = null;
    }
  }

  destroyLayoutMode() {
    if (this.layoutMode) {
      this.layoutMode.destroy();
      this.layoutMode = null;
    }
  }

  initZonesLayout() {
    this.currentSection = null;
    if (!RENDERER_MODE) {
      eventbus.emit('camera:zoom', ZOOM.LEVELS.SECTION, true);
      eventbus.emit('camera:pan', 0, 0);
    }
    // eventbus.emit('camera:pan', 0, 0);
  }

  initInsideZoneLayout() {
    this.initEditSectionMode(this.currentSection!);
  }

  initEditSectionMode(section: Section) {
    this.layoutMode = new EditSection(this, section);
  }

  getTransformToElement(pelem: SVGGraphicsElement, celem: SVGGraphicsElement) {
    return pelem.getScreenCTM()!.inverse().multiply(celem.getScreenCTM()!);
  }

  getTransformedCoords(e: MouseEvent | TouchEvent) {
    if (e instanceof MouseEvent) {
      this.pt!.x = e.clientX;
      this.pt!.y = e.clientY;
    } else {
      this.pt!.x = e.touches[0].pageX;
      this.pt!.y = e.touches[0].pageY;
    }

    return this.pt!.matrixTransform((this.svg.node as unknown as SVGSVGElement).getScreenCTM()!.inverse());
  }

  getGridPattern() {
    if (this.gridPattern) {
      return this.gridPattern;
    }
    const smallGridPattern = this.svg.pattern(25, 25, (add) => {
      add.path('M 25 0 L 0 0 0 25').attr({
        stroke: 'gray',
        'stroke-dasharray': '1, 2',
        fill: 'none',
        'stroke-width': '0.5',
      });
    });

    this.gridPattern = this.svg.pattern(75, 75, (add) => {
      add.rect(75, 75).attr({
        fill: smallGridPattern,
      });
      add.path('M 75 0 L 0 0 0 75').attr({
        id: 'grid-pattern',
        stroke: 'gray',
        'stroke-dasharray': '1, 3',
        fill: 'none',
        'stroke-width': '1',
      });
    });
    return this.gridPattern;
  }

  //check if category filteration failed : returns true on fail
  categoryFilterationFail(u: Section | Seat | GA | TableSeat) {
    if (this.props.categoryID && !u.attributes.categoryId) {
      return true;
    } else if (u.attributes.categoryId?.toString().trim() !== this.props.categoryID?.toString().trim()) {
      return true;
    }
    return false;
  }

  hasActiveCategory() {
    return this.props.categoryID != null && this.props.categoryID != 0;
  }

  isActiveCategory(categoryId: number | null | undefined) {
    return this.props.categoryID == categoryId;
  }

  sectionWouldChange(sid: string | undefined) {
    return this.currentSeatSection?.uuid !== sid;
  }

  //check if pos filteration failed : returns true on fail
  posFilterationFail(u: Seat | TableSeat) {
    if (
      this.props.posID &&
      u.attributes.posId &&
      u.attributes.posId.toString().trim() !== this.props.posID.toString().trim()
    ) {
      return true;
    }
    return false;
  }

  destroy() {
    delete this.pt;
    this.destroyEventListeners();
  }

  highlightCategory(categoryId: number) {
    this.seatmap.sections.forEach((section) => {
      if (section.status == SECTIONSTATUS.AVAILABLE && section.categoryId != categoryId) {
        section.whitenColor();
        //section.darkenColor();
      }
    });
  }

  unhighlightCategories() {
    this.seatmap.sections.forEach((section) => {
      section.unwhitenColor();
      //section.undarkenColor();
    });
  }

  clearSelectedSeats() {
    this.seatmap.clearSelection();
  }

  addSelectedSeatsToCart() {
    this.seatmap.addSelectedSeatsToCart();
  }

  selectSection(section: Section, dontTriggerCallback = true) {
    this.unselectSection(dontTriggerCallback);
    this.currentSeatSection = section;
    this.currentSeatSection.markSelection();

    if (!dontTriggerCallback && this.props.onSectionSwitch) {
      this.props.onSectionSwitch(section, true);
    }
  }

  unselectSection(dontTriggerCallback = true) {
    if (this.currentSeatSection) {
      const section = this.currentSeatSection;
      this.currentSeatSection.unmarkSelection();
      this.currentSeatSection = null;

      if (!dontTriggerCallback && this.props.onSectionSwitch) {
        this.props.onSectionSwitch(section, false);
      }
    }
  }

  toggleSelectedSection(section: Section) {
    const unselect = this.currentSeatSection?.uuid === section.uuid;

    if (unselect) {
      this.unselectSection(false);
    } else {
      this.selectSection(section, false);
    }

    return !unselect;
  }

  handleChangedConflictedZones() {
    this.seatmap.sections.forEach((section) => {
      section.updateConflictedZone();
    });
  }

  // FIXME: How does this work??
  zoomIntoSections(categoryId: number | number[]) {
    const categoryIds = Array.isArray(categoryId) ? categoryId : [categoryId];
    const sections = categoryIds.flatMap((categoryId) => this.seatmap.getSectionsByCategoryId(categoryId));

    if (!sections.length) return;

    // Get bbox of all sections together
    const bbox = sections.reduce((acc, curr) => {
      return acc.merge(curr.bbox()) as RBox;
    }, sections[0].bbox());

    this.zoomManager.zoomAndCenterBox(bbox);
    // this.highlightCategory(categoryId);
  }

  zoomAndCenterBox() {
    this.zoomManager.zoomAndCenterBox();
  }

  hasGap() {
    return store.state.renderer.hasGap;
  }
}
